import { useEffect, useMemo, useState } from 'react';
import { PSA_CLASSES, PSA_CLASS_LABELS, type CwProductClassAliasDto, type PsaClass } from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import { useCwProductClassAliases, useSetCwProductClassAliases } from './api.js';

interface AliasRow {
  /** Stable client-side key so rows keep focus while editing. */
  id: string;
  cwClassName: string;
  psaClass: PsaClass | '';
}

let aliasSeq = 0;
function newAliasId(): string {
  aliasSeq += 1;
  return `product-class-alias-${aliasSeq}`;
}

interface ProductClassAliasEditorProps {
  /** Whether the parent modal is open (gates the query). */
  isOpen: boolean;
}

/**
 * Maps a ConnectWise product class (a dropdown) onto a PSA product class (Agreement |
 * Service). On the products sync, a CW catalog item's class sets the synced product's
 * PSA class through these rows; a product whose class has no row keeps its
 * previous/blank PSA class (the sync never fails on a missing class alias).
 */
export function ProductClassAliasEditor({ isOpen }: ProductClassAliasEditorProps): JSX.Element {
  const configQuery = useCwProductClassAliases(isOpen);
  const saveAliases = useSetCwProductClassAliases();

  const [rows, setRows] = useState<AliasRow[]>([]);
  const config = configQuery.data;

  useEffect(() => {
    if (!config) return;
    setRows(
      config.aliases.map((alias) => ({
        id: newAliasId(),
        cwClassName: alias.cwClassName,
        psaClass: alias.psaClass,
      })),
    );
  }, [config]);

  const discoveredClasses = config?.discoveredClasses ?? [];
  const live = config?.live ?? false;

  const mappedNames = useMemo(
    () => new Set(rows.map((row) => row.cwClassName.trim().toLowerCase()).filter(Boolean)),
    [rows],
  );
  const unmappedDiscovered = discoveredClasses.filter(
    (name) => !mappedNames.has(name.trim().toLowerCase()),
  );

  const updateRow = (id: string, patch: Partial<AliasRow>): void => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removeRow = (id: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const addRow = (cwClassName = ''): void => {
    setRows((prev) => [...prev, { id: newAliasId(), cwClassName, psaClass: '' }]);
  };

  const handleSave = (): void => {
    const aliases: CwProductClassAliasDto[] = rows
      .filter((row): row is AliasRow & { psaClass: PsaClass } =>
        Boolean(row.cwClassName.trim() && row.psaClass),
      )
      .map((row) => ({ cwClassName: row.cwClassName.trim(), psaClass: row.psaClass }));
    saveAliases.mutate({ aliases });
  };

  const datalistId = 'cw-product-classes';

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">PSA class from ConnectWise class</h3>
        <p className="mt-1 text-sm text-slate-500">
          ConnectWise products carry a class (e.g. Agreement, Service, Inventory). Map each
          ConnectWise class to a PSA class below. On sync, a product&apos;s ConnectWise class sets
          its PSA class — a product whose class has no row here keeps its previous/blank PSA class.
        </p>
      </div>

      {configQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading product classes…</p>
      ) : configQuery.isError ? (
        <p className="text-sm text-rose-600">Failed to load product classes.</p>
      ) : (
        <>
          {live ? (
            <p className="text-xs text-emerald-600">
              ConnectWise product classes loaded live from your connection.
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              No ConnectWise product classes discovered yet. Configure the connection to load
              suggestions — you can still type a name.
            </p>
          )}

          <datalist id={datalistId}>
            {discoveredClasses.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          {saveAliases.isError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              Could not save the product-class aliases. Make sure each row targets a valid PSA
              class.
            </div>
          ) : null}

          {rows.length === 0 ? (
            <p className="text-sm text-slate-400">No product-class aliases yet. Add one below.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 pr-3 font-medium">ConnectWise class</th>
                  <th className="py-2 pr-3 font-medium">PSA class</th>
                  <th className="py-2 font-medium sr-only">Remove</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="py-2 pr-3 align-top">
                      <input
                        type="text"
                        list={datalistId}
                        value={row.cwClassName}
                        placeholder="e.g. Agreement"
                        onChange={(e) => updateRow(row.id, { cwClassName: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.psaClass}
                        onChange={(e) =>
                          updateRow(row.id, { psaClass: e.target.value as PsaClass | '' })
                        }
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <option value="">— Select class —</option>
                        {PSA_CLASSES.map((cls) => (
                          <option key={cls} value={cls}>
                            {PSA_CLASS_LABELS[cls]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 align-top">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="mt-1.5 text-xs font-medium text-slate-400 hover:text-rose-600"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => addRow()}>
              Add alias
            </Button>
            {unmappedDiscovered.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                <span>Unmapped:</span>
                {unmappedDiscovered.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => addRow(name)}
                    className="rounded-full border border-slate-300 bg-white px-2 py-0.5 hover:border-brand-400 hover:text-brand-700"
                  >
                    {name}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="ml-auto">
              <Button onClick={handleSave} isLoading={saveAliases.isPending} disabled={!config}>
                Save classes
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
